import type { QuestionnaireItem } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import * as structureDefinitions from '@seen/core/structureDefinitions'
import Fuse from 'fuse.js'
import { compact, partition, sortBy } from 'lodash-es'
import { type ReactNode, useMemo } from 'react'

export function getAnswerOptions(
  item: QuestionnaireItem
): { value: string; label: string }[] {
  return compact(
    item?.answerOption?.map((option) => {
      const prefix = option.extension?.find(
        (extension) =>
          extension.url ===
          structureDefinitions.QUESTIONNAIRE_OPTION_PREFIX_EXTENSION_URL
      )?.valueString
      const { code, display } = option.valueCoding ?? {}
      invariant(code, `${item.linkId} has an answer option without a coding.`)
      invariant(
        display,
        `${item.linkId} has an answer option without a display.`
      )
      return {
        value: code,
        label: prefix ? `${prefix}. ${display}` : display,
      }
    }) ?? []
  )
}

export interface ComboboxOption<TValue extends string | null> {
  value: TValue
  label: ReactNode
  keywords: string[]
  /** Rank weight: higher floats up in browse order and lifts text-match rank. */
  boost?: number
  // Listed but not selectable; the label must say why.
  disabled?: boolean
  // Search-rank weight. When set, a typed query uses this instead of `boost`,
  // so browse order can stay aggressive while a typed name still wins.
  searchBoost?: number
}

export interface SuggestedOption<
  TValue extends string | null,
> extends ComboboxOption<TValue> {
  confidence?: number
  reason?: string
  /** Pre-rendered tooltip content. When provided, this is used instead of confidence/reason. */
  tooltip?: ReactNode
}

export type IndexedOption<TValue extends string | null> =
  | ComboboxOption<TValue>
  | SuggestedOption<TValue>

export const COMBOBOX_NULL_OPTION = '__COMBOBOX_NULL_OPTION__'
export const COMBOBOX_UNDEFINED_VALUE = '---'
export const COMBOBOX_CREATE_VALUE = '__COMBOBOX_CREATE_VALUE__'

/** 50vh - search bar height - trigger height. 7rem is a rough estimate. */
export const COMBOBOX_LIST_MAX_HEIGHT = 'max-h-[calc(50vh-5rem)]'

/** If there are more than 8 options, the list requires scrolling and pre-sorting is helpful. */
export const NUM_OPTIONS_TO_SHOW_WITHOUT_PRE_SORTING = 8

/** Search performance is better with fewer options. */
export const MAX_OPTIONS_TO_SHOW = 50

/** Fuse-score reduction per boost point; small enough a clearly better text match still wins. */
export const BOOST_WEIGHT = 0.15

// Browse boosts of 3 and 4, times BOOST_WEIGHT, are larger than the gap between
// an exact name and a fuzzy hit on a role or organization string. Scaling them
// keeps that gap, and still orders two equal matches by the original boost.
const NAME_MATCH_BOOST_DIVISOR = 20

export function searchBoostPreservingNameMatch(browseBoost: number): number {
  return browseBoost / NAME_MATCH_BOOST_DIVISOR
}

interface UseComboboxFilteringOptions<TValue extends string | null> {
  options: ComboboxOption<TValue>[]
  suggestedOptions: SuggestedOption<TValue>[]
  search: string
  /** Fuse.js match threshold. 0 = exact match, 1 = match anything. Defaults to Fuse's default (0.6). */
  fuseThreshold?: number
  /** Optional grouping key. After Fuse ranking, items sharing a group key are placed adjacently at the rank of their highest-scoring member. Items returning undefined are passed through ungrouped. */
  groupBy?: (option: ComboboxOption<TValue>) => string | undefined
}

interface UseComboboxFilteringResult<TValue extends string | null> {
  suggestedValues: Set<TValue>
  regularOptions: ComboboxOption<TValue>[]
  allIndexedOptions: IndexedOption<TValue>[]
  filteredSuggestedOptions: SuggestedOption<TValue>[]
  filteredOptions: ComboboxOption<TValue>[]
}

export function useComboboxFiltering<TValue extends string | null>({
  options,
  suggestedOptions,
  search,
  fuseThreshold,
  groupBy,
}: UseComboboxFilteringOptions<TValue>): UseComboboxFilteringResult<TValue> {
  const suggestedValues = useMemo(
    () => new Set(suggestedOptions.map((o) => o.value)),
    [suggestedOptions]
  )

  const regularOptions = useMemo(
    () => options.filter((o) => !suggestedValues.has(o.value)),
    [options, suggestedValues]
  )

  const allIndexedOptions = useMemo(
    (): IndexedOption<TValue>[] => [...suggestedOptions, ...regularOptions],
    [suggestedOptions, regularOptions]
  )

  const fuse = useMemo(
    () =>
      new Fuse(allIndexedOptions, {
        keys: ['keywords'],
        fieldNormWeight: 0,
        ignoreLocation: true,
        includeScore: true,
        threshold: fuseThreshold ?? 0.6,
      }),
    [allIndexedOptions, fuseThreshold]
  )

  const { filteredSuggestedOptions, filteredOptions } = useMemo(() => {
    if (!search) {
      return {
        filteredSuggestedOptions: suggestedOptions,
        filteredOptions: regularOptions,
      }
    }
    const results = sortBy(
      fuse.search(search),
      (r) =>
        (r.score ?? 0) -
        (r.item.searchBoost ?? r.item.boost ?? 0) * BOOST_WEIGHT
    ).map((r) => r.item)
    const [suggested, regular] = partition(results, (o) =>
      suggestedValues.has(o.value)
    )
    const grouped = groupBy
      ? regroupAdjacent(regular as ComboboxOption<TValue>[], groupBy)
      : (regular as ComboboxOption<TValue>[])
    return {
      filteredSuggestedOptions: suggested as SuggestedOption<TValue>[],
      filteredOptions: grouped,
    }
  }, [search, fuse, suggestedOptions, regularOptions, suggestedValues, groupBy])

  return {
    suggestedValues,
    regularOptions,
    allIndexedOptions,
    filteredSuggestedOptions,
    filteredOptions,
  }
}

function regroupAdjacent<T>(
  items: T[],
  groupBy: (item: T) => string | undefined
): T[] {
  const buckets = new Map<string, T[]>()
  for (const item of items) {
    const key = groupBy(item)
    if (key === undefined) {
      continue
    }
    const bucket = buckets.get(key)
    if (bucket) {
      bucket.push(item)
    } else {
      buckets.set(key, [item])
    }
  }
  const emitted = new Set<string>()
  const result: T[] = []
  for (const item of items) {
    const key = groupBy(item)
    if (key === undefined) {
      result.push(item)
      continue
    }
    if (emitted.has(key)) {
      continue
    }
    emitted.add(key)
    const bucket = buckets.get(key)
    if (bucket) {
      result.push(...bucket)
    }
  }
  return result
}

export function fromComboboxValue<TValue extends string | null>(
  value: string
): TValue {
  return (value === COMBOBOX_NULL_OPTION ? null : value) as TValue
}

export function toComboboxValue<TValue extends string | null>(
  value: TValue
): string {
  return value ?? COMBOBOX_NULL_OPTION
}
